import { motion } from "framer-motion";
import { ChevronDown, Truck, Timer, BadgePercent } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../i18n/LanguageContext";

// Reusable masked line reveal — the line rises from below its own overflow-hidden container
const MaskedReveal = ({ children, delay = 0, duration = 0.9, className = "" }) => (
  <span className={`inline-block overflow-hidden align-baseline ${className}`}>
    <motion.span
      className="inline-block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const { t } = useLanguage();
  const scrollToGallery = () => {
    const element = document.querySelector("#gallery");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section 
      className="hero-section"
      data-testid="hero-section"
    >
      {/* Background image with subtle parallax scale-in */}
      <motion.div 
        className="hero-bg"
        role="img"
        aria-label={t("alts.heroBg")}
        initial={{ scale: 1.15 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1551854415-1df5f0d94b99?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      />

      {/* Gradient Overlay */}
      <div className="hero-gradient" />

      {/* Side accent lines — reveal from center */}
      <motion.div
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute left-8 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-[#009A49]/40 to-transparent hidden lg:block origin-center"
      />
      <motion.div
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute right-8 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-[#009A49]/40 to-transparent hidden lg:block origin-center"
      />

      {/* Content */}
      <div className="hero-content max-w-5xl mx-auto text-center">
        {/* Brand wordmark — line-by-line masked reveal (word by word) */}
        <h1
          className="font-heading text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold leading-[0.95] tracking-[0.05em] mb-6"
          data-testid="hero-wordmark"
        >
          <MaskedReveal delay={0.35} className="text-[#009A49] pb-2">LOCKER</MaskedReveal>
          <span className="inline-block w-4 md:w-6" />
          <MaskedReveal delay={0.55} className="text-white pb-2">ROOM</MaskedReveal>
        </h1>

        {/* LR monogram — reveals through a top→bottom mask (clip-path wipe) */}
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0 }}
          animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
          transition={{ duration: 1.1, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto mb-10 w-fit"
        >
          <img
            src="/lr-monogram.svg"
            alt={t("alts.monogram")}
            className="h-32 md:h-44 lg:h-52 w-auto"
            data-testid="hero-monogram"
          />
        </motion.div>

        {/* Tagline — masked reveal */}
        <p className="text-xs md:text-sm font-bold tracking-[0.4em] uppercase text-[#009A49] font-body mb-6" data-testid="hero-tagline">
          <MaskedReveal delay={1.35}>{t("hero.label")}</MaskedReveal>
        </p>

        {/* Shipping info — staggered masked reveals per line */}
        <div className="flex flex-col items-center gap-2 text-sm md:text-base text-zinc-300 font-body mb-12" data-testid="hero-shipping">
          <span className="flex items-center gap-2">
            <motion.span
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.55 }}
              className="inline-flex"
            >
              <Truck className="w-4 h-4 text-[#009A49]" />
            </motion.span>
            <MaskedReveal delay={1.55}>{t("hero.shippingLine1")}</MaskedReveal>
          </span>
          <span className="flex items-center gap-2">
            <motion.span
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 1.75 }}
              className="inline-flex"
            >
              <Timer className="w-4 h-4 text-[#009A49]" />
            </motion.span>
            <MaskedReveal delay={1.75}>{t("hero.shippingLine2")}</MaskedReveal>
          </span>
        </div>

        {/* CTA Button — soft rise + emerald glow */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 2, ease: [0.16, 1, 0.3, 1] }}
        >
          <Button
            onClick={scrollToGallery}
            className="btn-emerald px-10 py-6 text-base font-semibold tracking-wider uppercase rounded-full"
            data-testid="hero-cta"
          >
            {t("hero.cta")}
          </Button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4 }}
        className="absolute bottom-20 md:bottom-24 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-6 h-6 text-zinc-500" />
        </motion.div>
      </motion.div>

      {/* Promo banner — anchored to the bottom of the hero, always visible on first view */}
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 2.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-0 left-0 right-0 bg-[#009A49] py-3 overflow-hidden z-10"
        data-testid="promo-banner"
      >
        <div className="flex items-center justify-center gap-3 px-4">
          <BadgePercent className="w-5 h-5 text-white flex-shrink-0" />
          <p className="text-white font-body text-sm md:text-base font-semibold tracking-wide text-center uppercase">
            {t("promo.banner")}
          </p>
        </div>
      </motion.div>
    </section>
  );
};
